export const DEFAULT_RESEND_COOLDOWN_SECONDS = 30;
export const DEFAULT_RESEND_COOLDOWN_TEXT = 'Resend available in 30s';

const MIN_RESEND_COOLDOWN_SECONDS = 5;
const MAX_RESEND_COOLDOWN_SECONDS = 600;

/**
 * Matches the duration inside an admin-authored cooldown string, e.g. the `30s` of
 * "Resend available in 30s" or the `2 minutes` of "Try again in 2 minutes".
 *
 * The time unit is required, so a bare number elsewhere in the copy ("Resend up to 3
 * times", "Offer valid through 2026") is not mistaken for the cooldown. The trailing
 * `\b` keeps the unit from matching the first letter of an unrelated word ("3 more").
 * No `g` flag: `test`/`replace` must always look at the first duration only.
 */
const COOLDOWN_DURATION_PATTERN = /(\d+(?:\.\d+)?)\s*(m(?:in(?:ute)?s?)?|s(?:ec(?:ond)?s?)?)\b/i;

function clampCooldownSeconds(seconds: number): number {
  return Math.min(MAX_RESEND_COOLDOWN_SECONDS, Math.max(MIN_RESEND_COOLDOWN_SECONDS, seconds));
}

/**
 * Cooldown length for the live success page. The admin only configures the copy
 * ("Cooldown text"), so the countdown length is read back out of that copy and falls
 * back to 30s whenever it carries no usable duration.
 */
export function parseResendCooldownSeconds(cooldownText?: string | null): number {
  const match = COOLDOWN_DURATION_PATTERN.exec((cooldownText || '').trim());
  if (!match) {
    return DEFAULT_RESEND_COOLDOWN_SECONDS;
  }

  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) {
    return DEFAULT_RESEND_COOLDOWN_SECONDS;
  }

  const seconds = match[2].toLowerCase().startsWith('m') ? amount * 60 : amount;
  return clampCooldownSeconds(Math.round(seconds));
}

/** Rewrites the admin cooldown copy so it shows the seconds actually remaining. */
export function formatResendCooldownText(
  cooldownText: string | null | undefined,
  remainingSeconds: number
): string {
  const seconds = Math.max(0, Math.ceil(remainingSeconds));
  const text = (cooldownText || '').trim();

  if (!text) {
    return `Resend available in ${seconds}s`;
  }
  if (COOLDOWN_DURATION_PATTERN.test(text)) {
    return text.replace(COOLDOWN_DURATION_PATTERN, `${seconds}s`);
  }
  return `${text} (${seconds}s)`;
}

export function formatResendAttemptsLabel(attemptsLeft: number): string {
  const left = Math.max(0, Math.floor(attemptsLeft));
  if (left <= 0) {
    return 'No resend attempts left';
  }
  return `${left} ${left === 1 ? 'try' : 'tries'} left`;
}

export interface ResendStatusOptions {
  cooldownText?: string | null;
  cooldownRemainingSeconds: number;
  attemptsLeft: number;
}

/** Status line under the live success page's Resend button. */
export function buildResendStatusText(options: ResendStatusOptions): string {
  const attemptsLabel = formatResendAttemptsLabel(options.attemptsLeft);
  if (options.attemptsLeft <= 0) {
    return attemptsLabel;
  }
  if (options.cooldownRemainingSeconds > 0) {
    const cooldown = formatResendCooldownText(
      options.cooldownText,
      options.cooldownRemainingSeconds
    );
    return `${cooldown} · ${attemptsLabel}`;
  }
  return attemptsLabel;
}

/**
 * Status line for the campaign editor preview, which renders the saved configuration
 * rather than a running countdown.
 */
export function buildResendPreviewStatusText(options: {
  maxAttempts: number;
  cooldownText?: string | null;
}): string {
  const triesLabel = options.maxAttempts === 1 ? 'try' : 'tries';
  const cooldown = options.cooldownText?.trim() || DEFAULT_RESEND_COOLDOWN_TEXT;
  return `${cooldown} · ${options.maxAttempts} ${triesLabel}`;
}
